import type { ThemeConfig } from "antd";
import { colors, control, fontFamily, fontSize, radius, space } from "./foundation";

const theme: ThemeConfig = {
  token: {
    colorPrimary: colors.primary,
    colorPrimaryHover: colors["primary-deep"],
    colorPrimaryActive: colors["primary-deep"],
    colorLink: colors["primary-deep"],
    colorLinkHover: colors["primary-press"],
    colorLinkActive: colors["primary-press"],
    colorSuccess: colors.success,
    colorSuccessBg: colors["success-bg"],
    colorSuccessBorder: colors["success-border"],
    colorSuccessText: colors["success-text"],
    colorWarning: colors.warning,
    colorWarningBg: colors["warning-bg"],
    colorWarningBorder: colors["warning-border"],
    colorWarningText: colors.warning,
    colorError: colors.error,
    colorErrorBg: colors["error-bg"],
    colorErrorBorder: colors["error-border"],
    colorErrorText: colors.error,
    colorInfo: colors.info,
    colorInfoBg: colors["info-bg"],
    colorInfoBorder: colors["info-border"],
    colorBgContainer: colors.canvas,
    colorBgLayout: colors["soft-cloud"],
    colorText: colors.ink,
    colorTextSecondary: colors.charcoal,
    colorTextTertiary: colors.ash,
    colorBorder: colors.hairline,
    colorBorderSecondary: colors["border-light"],
    borderRadius: radius.control,
    borderRadiusLG: radius.card,
    borderRadiusSM: radius.small,
    fontFamily,
    fontSize: fontSize.body,
    fontSizeHeading1: fontSize.hero,
    fontSizeHeading2: fontSize.title,
    fontSizeHeading3: fontSize.heading,
    fontSizeHeading4: fontSize.subheading,
    lineHeight: 1.5,
    controlHeight: control.normal,
    controlHeightLG: control.large,
    controlHeightSM: control.small,
    fontWeightStrong: 700,
  },
  components: {
    Button: {
      borderRadiusSM: radius.control,
      borderRadiusLG: radius.control,
      controlHeight: control.normal,
      controlHeightLG: control.large,
      controlHeightSM: control.small,
      borderRadius: radius.control,
      fontWeight: 500,
      // White text needs the deep red for AA; see foundation.ts.
      colorPrimary: colors["primary-deep"],
      colorPrimaryHover: colors["primary-press"],
      colorPrimaryActive: colors["primary-press"],
      primaryShadow: "none",
      defaultShadow: "none",
    },
    Input: {
      borderRadiusSM: radius.control,
      borderRadiusLG: radius.control,
      controlHeight: control.normal,
      borderRadius: radius.control,
      activeBorderColor: colors.ink,
      activeShadow: `0 0 0 2px ${colors.ink}`,
    },
    InputNumber: {
      borderRadiusSM: radius.control,
      borderRadiusLG: radius.control,
      controlHeight: control.normal,
      borderRadius: radius.control,
      activeBorderColor: colors.ink,
      activeShadow: `0 0 0 2px ${colors.ink}`,
    },
    Select: {
      borderRadiusSM: radius.control,
      borderRadiusLG: radius.control,
      controlHeight: control.normal,
      borderRadius: radius.control,
    },
    DatePicker: {
      borderRadiusSM: radius.control,
      borderRadiusLG: radius.control,
      controlHeight: control.normal,
      borderRadius: radius.control,
      activeBorderColor: colors.ink,
      activeShadow: `0 0 0 2px ${colors.ink}`,
    },
    Card: {
      borderRadius: radius.card,
      borderRadiusLG: radius.card,
      headerFontSize: fontSize.bodyLarge,
      headerFontSizeSM: fontSize.bodyLarge,
      bodyPadding: space[6],
      headerPadding: space[6],
      bodyPaddingSM: space[4],
      headerPaddingSM: space[4],
      headerHeightSM: control.large,
    },
    Form: {
      itemMarginBottom: space[5],
      verticalLabelPadding: `0 0 ${space[2]}px`,
    },
    Modal: {
      borderRadiusLG: radius.card,
    },
    Tag: {
      borderRadiusSM: radius.pill,
    },
    Tabs: {
      itemActiveColor: colors.ink,
      itemSelectedColor: colors.ink,
      inkBarColor: colors.ink,
      itemHoverColor: colors.ink,
    },
    // The active page number is text: deep red, never Rausch (3.5:1 on white).
    Pagination: {
      colorPrimary: colors["primary-deep"],
      colorPrimaryHover: colors["primary-press"],
    },
    Table: {
      headerBg: colors["soft-cloud"],
      headerColor: colors.charcoal,
      borderColor: colors.hairline,
    },
    Layout: {
      headerBg: colors.canvas,
      siderBg: colors.canvas,
      bodyBg: colors["soft-cloud"],
    },
  },
};

export default theme;
